/* My Clean Supplies helper tools (2026-10 SEO programme).
   Six Web Components, one per tool page. All the arithmetic lives in MCSToolMath, which
   setup/scripts/mcs-seo/tools/helper_math_check.cjs also loads to test it against known cases.
   Sources for every constant are named next to it and on the page. No network calls. */
(function (root) {
  'use strict';

  var LN = Math.log;

  var M = {
    /* NIST SP 811, Appendix B.9: inch of water, conventional (inH2O) = 2.490 889 E+02 Pa */
    PA_PER_IN_WC: 249.0889,
    /* NIST SP 811, Appendix B.9: inch of mercury, conventional (inHg) = 3.386 389 E+03 Pa */
    HPA_PER_IN_HG: 33.86389,
    /* NIST SP 811, Appendix B.9: grain = 6.479 891 E-05 kg, pound (avoirdupois) = 0.453 592 37 kg -> 7,000 grains per pound */
    GRAINS_PER_LB: 7000,
    /* NIST SP 811, Appendix B.9: standard atmosphere = 101 325 Pa = 29.921 inHg */
    STD_IN_HG: 29.92,

    round: function (x, d) {
      var f = Math.pow(10, d || 0);
      return Math.round(x * f) / f;
    },

    /* ---------- Air changes per hour ---------- */
    volume: function (l, w, h) { return l * w * h; },
    /* ACH = CFM x 60 / cubic feet, so CFM = cubic feet x ACH / 60 */
    cfmNeeded: function (cuft, ach) { return cuft * ach / 60; },
    achFrom: function (cfm, cuft) { return cuft > 0 ? cfm * 60 / cuft : 0; },
    /* usable airflow per machine after the user's airflow allowance (percent) */
    usableCfm: function (ratedCfm, allowancePct) { return ratedCfm * (1 - (allowancePct || 0) / 100); },
    machinesNeeded: function (cfmNeeded, ratedCfm, allowancePct) {
      var u = M.usableCfm(ratedCfm, allowancePct);
      return u > 0 ? Math.max(1, Math.ceil(cfmNeeded / u - 1e-9)) : 0;
    },
    /* CDC Guidelines for Environmental Infection Control in Health-Care Facilities, Appendix B,
       Table B.1 (perfect mixing): t = ln(C2/C1) / (Q/V) x 60 minutes */
    removalMinutes: function (ach, efficiencyPct) {
      if (!(ach > 0)) return 0;
      return LN(1 / (1 - efficiencyPct / 100)) / ach * 60;
    },

    /* ---------- Dehumidifier sizing ---------- */
    /* IICRC "Initial Dehumidification Recommendation Factors and Formulas", Imperial rev. 3.1.22:
       refrigerant: cubic feet / chart factor = pints per day (AHAM); desiccant: cubic feet x ACH / 60 = CFM */
    IICRC_FACTORS: {
      conventional: [100, 40, 30, null],
      lgr: [100, 50, 40, 40],
      desiccant: [1, 2, 3, 3]
    },
    iicrcDehu: function (cuft, cls, type) {
      var f = (M.IICRC_FACTORS[type] || [])[cls - 1];
      if (f == null) return null;
      if (type === 'desiccant') return { kind: 'cfm', value: cuft * f / 60, factor: f };
      return { kind: 'ppd', value: cuft / f, factor: f };
    },
    /* AHAM dehumidifier sizing table as AlorAir publishes it (pints per day, AHAM rating) */
    AHAM_AREAS: [500, 1000, 1500, 2000, 2500],
    AHAM_TABLE: {
      moderate: [10, 14, 18, 22, 26],
      very: [12, 17, 22, 27, 32],
      wet: [14, 20, 26, 32, 38],
      extreme: [16, 23, 30, 37, 44]
    },
    ahamPints: function (sqft, cond) {
      var row = M.AHAM_TABLE[cond], a = M.AHAM_AREAS;
      if (!row || !(sqft > 0)) return null;
      if (sqft <= a[0]) return { pints: row[0], extended: false };
      for (var i = 1; i < a.length; i++) {
        if (sqft <= a[i]) {
          return { pints: row[i - 1] + (row[i] - row[i - 1]) * (sqft - a[i - 1]) / (a[i] - a[i - 1]), extended: false };
        }
      }
      /* past 2,500 sq ft: keep the table's last step per 500 sq ft (our rule of thumb) */
      var step = row[4] - row[3];
      return { pints: row[4] + step * (sqft - a[4]) / 500, extended: true };
    },
    unitsFor: function (total, perUnit) {
      return perUnit > 0 ? Math.max(1, Math.ceil(total / perUnit - 1e-9)) : 0;
    },

    /* ---------- Psychrometrics ---------- */
    fToC: function (f) { return (f - 32) * 5 / 9; },
    cToF: function (c) { return c * 9 / 5 + 32; },
    /* National Weather Service (weather.gov) "Vapor Pressure": es = 6.11 x 10^(7.5 T / (237.3 + T)), hPa, T in C */
    satVp: function (tC) { return 6.11 * Math.pow(10, 7.5 * tC / (237.3 + tC)); },
    /* the same formula solved for temperature: dew point from actual vapor pressure */
    dewPointC: function (e) {
      var L = Math.log(e / 6.11) / Math.LN10;
      return 237.3 * L / (7.5 - L);
    },
    /* National Weather Service "Mixing Ratio": w = 621.97 x e / (P - e), grams per kilogram */
    mixingRatio: function (e, pHpa) { return 621.97 * e / (pHpa - e); },
    /* grams per kilogram -> grains per pound: g/kg / 1000 = lb/lb, x 7,000 grains per pound */
    gpp: function (gPerKg) { return gPerKg / 1000 * M.GRAINS_PER_LB; },
    psychro: function (o) {
      var tC = M.fToC(o.tF);
      var p = (o.pInHg || M.STD_IN_HG) * M.HPA_PER_IN_HG;
      var es = M.satVp(tC);
      var e, rh;
      if (o.dewF != null) {
        e = M.satVp(M.fToC(o.dewF));
        rh = 100 * e / es;
      } else {
        rh = o.rh;
        e = es * rh / 100;
      }
      var w = M.mixingRatio(e, p);
      return {
        rh: rh,
        dewF: M.cToF(M.dewPointC(e)),
        eHpa: e,
        eInHg: e / M.HPA_PER_IN_HG,
        gPerKg: w,
        lbPerLb: w / 1000,
        gpp: M.gpp(w)
      };
    },

    /* ---------- Pressure ---------- */
    paToInWc: function (pa) { return pa / M.PA_PER_IN_WC; },
    inWcToPa: function (inwc) { return inwc * M.PA_PER_IN_WC; },

    /* ---------- Air movers (our rule of thumb, adjustable divisors) ---------- */
    airMovers: function (o) {
      var floor = Math.max(0, o.floor || 0), upper = Math.max(0, o.upper || 0), extra = Math.max(0, o.offsets || 0);
      var fMin = o.floorLow || 70, fMax = o.floorHigh || 50, uMin = o.upperLow || 150, uMax = o.upperHigh || 100;
      var c = function (x) { return x > 0 ? Math.ceil(x - 1e-9) : 0; };
      var floorLo = c(floor / fMin), floorHi = c(floor / fMax), upLo = c(upper / uMin), upHi = c(upper / uMax);
      return { floorLo: floorLo, floorHi: floorHi, upperLo: upLo, upperHi: upHi, extra: extra, min: floorLo + upLo + extra, max: floorHi + upHi + extra };
    },

    /* ---------- Floor pads (3M "Choose the right floor pad for the job", May 2025) ---------- */
    PAD_SPEEDS: {
      strip: [175, 600], scrub: [175, 600], clean: [175, 600], buff: [175, 600],
      burnish: [1500, Infinity], gloss: [1500, Infinity], soft: [1500, Infinity],
      stone_condition: [175, Infinity], stone_polish: [175, Infinity]
    },
    padJobsForRpm: function (rpm) {
      var out = [];
      Object.keys(M.PAD_SPEEDS).forEach(function (k) {
        var r = M.PAD_SPEEDS[k];
        if (rpm >= r[0] && rpm <= r[1]) out.push(k);
      });
      return out;
    }
  };

  root.MCSToolMath = M;
  if (!root.customElements || !root.HTMLElement || !root.document) return;

  function fmt(x, d) {
    if (!isFinite(x)) return '-';
    return Number(x).toLocaleString('en-US', { minimumFractionDigits: d || 0, maximumFractionDigits: d || 0 });
  }

  class McsCalc extends root.HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var self = this;
      this.addEventListener('input', function () { self.update(); });
      this.addEventListener('change', function () { self.update(); });
      this.setup();
      this.update();
    }
    setup() {}
    field(name) { return this.querySelector('[name="' + name + '"]'); }
    num(name, fallback) {
      var el = this.field(name);
      var v = el ? parseFloat(String(el.value).replace(/,/g, '')) : NaN;
      return isFinite(v) ? v : fallback;
    }
    pick(name) {
      var el = this.querySelector('[name="' + name + '"]:checked') || this.field(name);
      return el ? el.value : '';
    }
    out(key, text) {
      this.querySelectorAll('[data-out="' + key + '"]').forEach(function (n) { n.textContent = text; });
    }
    show(key, on) {
      this.querySelectorAll('[data-show="' + key + '"]').forEach(function (n) { n.hidden = !on; });
    }
  }

  /* 1. Air changes per hour */
  class McsAchCalc extends McsCalc {
    setup() {
      var self = this, sel = this.field('machine');
      if (sel) sel.addEventListener('change', function () {
        var o = sel.options[sel.selectedIndex];
        var cfm = o && o.getAttribute('data-cfm');
        if (cfm) self.field('unit_cfm').value = cfm; self.update();
      });
    }
    update() {
      var mode = this.pick('mode') || 'size';
      var vol = M.volume(this.num('length', 0), this.num('width', 0), this.num('height', 0));
      var unit = this.num('unit_cfm', 0), allow = this.num('allowance', 0);
      this.show('size', mode === 'size');
      this.show('check', mode === 'check');
      this.out('volume', fmt(vol));
      if (mode === 'size') {
        var ach = this.num('ach', 0);
        var need = M.cfmNeeded(vol, ach);
        var n = M.machinesNeeded(need, unit, allow);
        var got = M.achFrom(n * M.usableCfm(unit, allow), vol);
        this.out('cfm', fmt(need));
        this.out('machines', fmt(n));
        this.out('machines_word', n === 1 ? 'machine' : 'machines');
        this.out('usable', fmt(M.usableCfm(unit, allow)));
        this.out('delivered', fmt(got, 1));
        this.out('t99', fmt(M.removalMinutes(ach, 99)));
        this.out('t999', fmt(M.removalMinutes(ach, 99.9)));
      } else {
        var count = Math.max(1, Math.round(this.num('count', 1)));
        var ach2 = M.achFrom(count * M.usableCfm(unit, allow), vol);
        this.out('ach_result', fmt(ach2, 1));
        this.out('t99b', fmt(M.removalMinutes(ach2, 99)));
      }
    }
  }

  /* 2. Dehumidifier size */
  class McsDehuCalc extends McsCalc {
    setup() {
      var self = this, sel = this.field('unit');
      if (sel) sel.addEventListener('change', function () {
        var o = sel.options[sel.selectedIndex];
        var p = o && o.getAttribute('data-pints');
        if (p) self.field('unit_pints').value = p; self.update();
      });
    }
    update() {
      var mode = this.pick('mode') || 'room';
      this.show('room', mode === 'room');
      this.show('water', mode === 'water');
      var per = this.num('unit_pints', 0);
      if (mode === 'room') {
        var r = M.ahamPints(this.num('sqft', 0), this.pick('condition'));
        var pints = r ? r.pints : 0;
        this.out('pints', fmt(Math.ceil(pints - 1e-9)));
        this.show('extended', !!(r && r.extended));
        var n = M.unitsFor(pints, per);
        this.out('units', fmt(n));
        this.out('units_word', n === 1 ? 'unit' : 'units');
      } else {
        var cuft = M.volume(this.num('length', 0), this.num('width', 0), this.num('height', 0));
        var type = this.pick('type'), cls = parseInt(this.pick('class'), 10) || 2;
        var res = M.iicrcDehu(cuft, cls, type);
        this.out('cuft', fmt(cuft));
        this.show('na', res === null);
        this.show('ppd_out', !!res && res.kind === 'ppd');
        this.show('cfm_out', !!res && res.kind === 'cfm');
        if (res && res.kind === 'ppd') {
          var n2 = M.unitsFor(res.value, per);
          this.out('factor', fmt(res.factor));
          this.out('ppd', fmt(res.value, 1));
          this.out('units2', fmt(n2));
          this.out('units2_word', n2 === 1 ? 'unit' : 'units');
        } else if (res) {
          this.out('dach', fmt(res.factor));
          this.out('dcfm', fmt(res.value));
        }
      }
    }
  }

  /* 3. Psychrometric */
  class McsPsychroCalc extends McsCalc {
    reading(prefix) {
      var by = this.pick(prefix + 'by') || 'rh';
      var o = { tF: this.num(prefix + 'temp', 70), pInHg: this.num('pressure', M.STD_IN_HG) };
      if (by === 'dew') o.dewF = this.num(prefix + 'dew', 50); else o.rh = this.num(prefix + 'rh', 50);
      this.show(prefix + 'rh_field', by !== 'dew');
      this.show(prefix + 'dew_field', by === 'dew');
      return o;
    }
    update() {
      var a = M.psychro(this.reading('a_'));
      this.out('gpp', fmt(a.gpp, 1));
      this.out('dew', fmt(a.dewF, 1));
      this.out('rh', fmt(a.rh, 1));
      this.out('vp_inhg', fmt(a.eInHg, 3));
      this.out('vp_hpa', fmt(a.eHpa, 2));
      this.out('ratio', fmt(a.lbPerLb, 5));
      var two = this.pick('compare') === 'yes';
      this.show('second', two);
      if (two) {
        var b = M.psychro(this.reading('b_'));
        this.out('gpp_b', fmt(b.gpp, 1));
        this.out('dew_b', fmt(b.dewF, 1));
        this.out('depression', fmt(a.gpp - b.gpp, 1));
      }
    }
  }

  /* 4. Pascals and inches of water */
  class McsPressureConv extends McsCalc {
    setup() {
      var self = this;
      this._last = 'inwc';
      ['pa', 'inwc'].forEach(function (n) {
        var el = self.field(n);
        if (el) el.addEventListener('input', function () { self._last = n; });
      });
    }
    update() {
      var pa, inwc;
      if (this._last === 'pa') {
        pa = this.num('pa', 0);
        inwc = M.paToInWc(pa);
        this.field('inwc').value = M.round(inwc, 5);
      } else {
        inwc = this.num('inwc', 0);
        pa = M.inWcToPa(inwc);
        this.field('pa').value = M.round(pa, 3);
      }
      this.out('pa_text', fmt(pa, 2));
      this.out('inwc_text', fmt(inwc, 4));
      this.out('mmwc_text', fmt(pa / 9.80665, 2));
    }
  }

  /* 5. Air movers */
  class McsAirMoverCalc extends McsCalc {
    update() {
      var r = M.airMovers({
        floor: this.num('floor', 0), upper: this.num('upper', 0), offsets: this.num('offsets', 0),
        floorLow: this.num('floor_low', 70), floorHigh: this.num('floor_high', 50),
        upperLow: this.num('upper_low', 150), upperHigh: this.num('upper_high', 100)
      });
      this.out('min', fmt(r.min));
      this.out('max', fmt(r.max));
      this.out('floor_range', r.floorLo === r.floorHi ? fmt(r.floorLo) : fmt(r.floorLo) + ' to ' + fmt(r.floorHi));
      this.out('upper_range', r.upperLo === r.upperHi ? fmt(r.upperLo) : fmt(r.upperLo) + ' to ' + fmt(r.upperHi));
      this.out('extra', fmt(r.extra));
      var cuft = this.num('room_sqft', 0) * this.num('height', 8);
      var d = M.iicrcDehu(cuft, parseInt(this.pick('class'), 10) || 2, 'lgr');
      this.out('cuft', fmt(cuft));
      this.out('ppd', d ? fmt(d.value, 1) : '-');
    }
  }

  /* 6. Floor pad chart: highlight the rows a machine speed can run */
  class McsPadChart extends McsCalc {
    setup() {
      var self = this, sel = this.field('machine');
      if (sel) sel.addEventListener('change', function () {
        var o = sel.options[sel.selectedIndex];
        var rpm = o && o.getAttribute('data-rpm');
        if (rpm) self.field('rpm').value = rpm; self.update();
      });
      this.querySelectorAll('[data-print]').forEach(function (b) {
        b.hidden = false;
        b.addEventListener('click', function () { root.print(); });
      });
    }
    update() {
      var rpm = this.num('rpm', 0);
      var jobs = rpm > 0 ? M.padJobsForRpm(rpm) : [];
      var rows = this.querySelectorAll('tr[data-job]');
      var n = 0;
      rows.forEach(function (tr) {
        var on = jobs.indexOf(tr.getAttribute('data-job')) !== -1;
        tr.classList.toggle('is-match', on && rpm > 0);
        tr.classList.toggle('is-dim', !on && rpm > 0);
        if (on) n++;
      });
      if (!(rpm > 0)) this.out('rpm_note', 'Enter your machine speed to highlight the pads it can run.');
      else if (!jobs.length) this.out('rpm_note', fmt(rpm) + ' RPM sits between 3M\'s low speed (175 to 600 RPM) and ultra high speed (1,500 RPM and above) bands: only the stone floor pads list that range.');
      else this.out('rpm_note', fmt(rpm) + ' RPM fits ' + n + ' of the ' + rows.length + ' jobs on 3M\'s chart (highlighted).');
    }
  }

  var defs = {
    'mcs-ach-calc': McsAchCalc,
    'mcs-dehu-calc': McsDehuCalc,
    'mcs-psychro-calc': McsPsychroCalc,
    'mcs-pressure-conv': McsPressureConv,
    'mcs-airmover-calc': McsAirMoverCalc,
    'mcs-pad-chart': McsPadChart
  };
  Object.keys(defs).forEach(function (tag) {
    if (!root.customElements.get(tag)) root.customElements.define(tag, defs[tag]);
  });
})(typeof window !== 'undefined' ? window : this);
